import React from "react";
import { useQueryClient } from "@tanstack/react-query";import { Link } from "@tanstack/react-router";
import { staffPersonaQueryKey, useStaffPersona } from "../queries";
import { Avatar, AvatarProps, Button, Group, HoverCard, Skeleton, Stack, Text, TextProps, FloatingPosition } from "@mantine/core";
import { StaffAvatar } from "./StaffAvatar";
import { TeamsMessagePersonButton } from "@features/send-teams-message/components/TeamsMessagePersonButton";
import { IconUser } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { toAvatarSize, toTextSize } from "../helpers";
import { useViewMode } from "@stores/usePortalModeStore";
import { getPortalsApiResourceUrl } from "@services/api/portalApi";

interface PersonaProps {
    identifier: {
        type: 'initials' | 'staffId' | 'oid';
        value: string;
    };
    initialsStyle?: 'none' | 'default' | 'dimmed';
    nameStyle?: 'fullName' | 'initials';
    size?: number;
    textProps?: TextProps;
    avatarProps?: AvatarProps;
    reverseOrder?: boolean;
    containerProps?: React.HTMLAttributes<HTMLDivElement>;
    includePhoto?: boolean;
    subtitle?: 'jobTitle' | 'email' | 'custom' | 'none';
    customSubtitle?: React.ReactNode
    clickable?: boolean;
    hoverable?: boolean;
    dropDownPosition?: FloatingPosition;
};

export function StaffPersona({ identifier, initialsStyle = 'none', nameStyle = 'fullName', size = 32, textProps, avatarProps, reverseOrder = false, containerProps, includePhoto = true, subtitle = 'none', customSubtitle, clickable = true, hoverable = true, dropDownPosition = "top" }: PersonaProps) {    const qc = useQueryClient();

    const { data, isLoading } = useStaffPersona({ identifier: identifier.value, identifierType: identifier.type });
    const navigate = useNavigate()

    // Track retry/failure by stable resource path (without token query params).
    const failedResourcesRef = React.useRef<Set<string>>(new Set());
    const refreshedResourcesRef = React.useRef<Set<string>>(new Set());

    // The actual src we render (sticky).
    const [imgSrc, setImgSrc] = React.useState<string | null>(null);

    const photoUrl = data?.photo?.url ?? null;
    const photoResource = photoUrl ? photoUrl.split("?")[0] : null;

    const viewMode = useViewMode();

    // When we get a NEW signed URL, use it unless this resource is known-bad.
    React.useEffect(() => {
        if (!photoUrl) {
            setImgSrc(null);
            return;
        }

        if (photoResource && failedResourcesRef.current.has(photoResource)) {
            setImgSrc(null);
            return;
        }

        setImgSrc(photoUrl);
    }, [photoUrl, photoResource]);

    if (isLoading || !data) {
        const avatarSkeletonSize = toAvatarSize(avatarProps?.size, size);
        const textSkeletonHeight = toTextSize(textProps?.size);
        const textSkeletonLength = nameStyle === 'initials' ? 3 : 10;
        const textSkeletonWidth = typeof textSkeletonHeight === "number"
            ? textSkeletonHeight * textSkeletonLength
            : `calc(${textSkeletonHeight} * ${textSkeletonLength})`;

        return (
        <div style={{ display: "flex", gap: containerProps?.style?.gap ?? 10, alignItems: "center" }}>
            <Skeleton width={avatarSkeletonSize} height={avatarSkeletonSize} radius="xl" />
            <Skeleton width={textSkeletonWidth} height={textSkeletonHeight} radius={6} />
        </div>
        );
    }

    const displayName = `${data.firstName} ${data.surname}`;
    const { className, style, ...restContainerProps } = containerProps ?? {};
    const rootClassName = `${className ? `${className} ` : ""} shrink-0`;
    const rootStyle: React.CSSProperties = {
        display: "flex",
        gap: 10,
        alignItems: "center",
        justifyItems: "start",
        flexDirection: reverseOrder ? "row-reverse" : "row",
        flexWrap: "nowrap",
        width: "fit-content",
        ...style,
    };

    const persona = (
        <>
            {includePhoto && (
                <Avatar
                    name={displayName}
                    color='dark'
                    size={avatarProps?.size ?? 'sm'}
                    src={imgSrc ? getPortalsApiResourceUrl(imgSrc) : undefined}
                    onError={() => {
                        // Always show fallback immediately.
                        setImgSrc(null);

                        if (!photoResource) {
                            return;
                        }

                        // Retry once for this resource to recover from expired signatures.
                        if (!refreshedResourcesRef.current.has(photoResource)) {
                            refreshedResourcesRef.current.add(photoResource);
                            qc.invalidateQueries({ queryKey: staffPersonaQueryKey({ identifier: identifier.value, identifierType: identifier.type as 'initials' | 'staffId' | 'oid' }) });
                            return;
                        }

                        // Permanent failure for this resource in this component instance.
                        failedResourcesRef.current.add(photoResource);
                    }}
                    {...avatarProps}
                />
            )}
            <Stack gap={0}>
                <Text fw={500} size="sm" style={{ whiteSpace: 'nowrap'}} {...textProps}>
                    {nameStyle === 'initials' ? data.initials : displayName} <Text span c={initialsStyle === 'dimmed' ? 'dimmed' : undefined}>{nameStyle !== 'initials' && initialsStyle !== 'none' ? `(${data.initials})` : null}</Text>
                </Text>
                {subtitle === 'jobTitle' && <Text size="xs" c="dimmed" {...textProps}>{data.jobTitle}</Text>}
                {subtitle === 'email' && <Text size="xs" c="dimmed" {...textProps}>{data.initials}@godalming.ac.uk</Text>}
                {subtitle === 'custom' && customSubtitle}
            </Stack>
        </>
    );

    const target = !clickable || viewMode !== 'staff' ? (
        <div className={rootClassName} style={rootStyle} {...restContainerProps}>
            {persona}
        </div>
    ) : (
        <Link
            to="/staff/$initials"
            params={{ initials: data.initials }}
            preload="intent"
            className={rootClassName}
            style={{ ...rootStyle, color: "inherit", textDecoration: "none" }}
            {...(restContainerProps as any)}
        >
            {persona}
        </Link>
    );

    return (
        hoverable && viewMode === 'staff' ? (
        <HoverCard 
            shadow="md" 
            withArrow 
            hideDetached={true} 
            floatingStrategy="fixed"
            offset={0}
            closeDelay={50}
            position={dropDownPosition}
            zIndex={50}
        >
            <HoverCard.Target>
                {target}
            </HoverCard.Target>
            <HoverCard.Dropdown>
                <Stack>
                    <Group>
                        <StaffAvatar identifier={{ value: identifier.value, type: identifier.type }} size={100} />
                        <Stack gap={0}>
                            <Text size="lg" fw={500}>{displayName}</Text>
                            {data.jobTitle && <Text size="xs" c="dimmed" maw={250}>{data.jobTitle}</Text>}
                        </Stack>
                    </Group>
                    <Group>
                        <Button 
                            leftSection={<IconUser size={16}/>}
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                navigate({ to: '/staff/$initials', params: { initials: data.initials }})
                            }}
                            size="xs"
                        >
                            View full profile
                        </Button>
                        <TeamsMessagePersonButton recipient={data.oid} />
                    </Group>
                </Stack>
            </HoverCard.Dropdown>
        </HoverCard>
        ) : (
            target
        )
    );
}
